<template>
  <div
    class="place-card"
    :class="{
      childActivity: isChildActivity,
      parentActivity: hasChildren,
    }"
    v-if="!isRecommendation"
  >
    <ExpItem :activity="activity" @click="$emit('image-click')" v-if="hasChildren && !isLoading" />

    <div v-if="!hasChildren">
      <div :class="{loading: isLoading}">
        <transition name="fade-in">
          <div class="content">
            <div class="image-placeholder" @click="$emit('image-click')">
              <slot>
                <div class="mb-s placeImage">
                  <img v-if="!isLoading" :src="activityImg" alt="" />
                </div>
              </slot>
            </div>
            <div class="details">
              <div class="selected-text" v-if="!isLoading">Selected</div>
              <div class="rating-wrapper" v-if="isRatingShown">
                <StarRating
                  class="star-rating"
                  v-if="rating"
                  :rating="Number(rating)"
                  read-only
                  :star-size="12"
                  :increment="0.1"
                />
              </div>
              <div class="name" v-html="formattedName"></div>
              <div class="activity-description">
                <span class="t-copy sm mb-s" v-if="description">{{ description }}</span>
              </div>
              <div class="itinerary-card-tags">
                <div class="tag" v-for="tag in activityTags" :key="tag.name" :class="{selected: tag.isActive}">
                  {{ tag.name }}
                </div>
              </div>
            </div>

            <div class="cost">
              <div class="price-wrapper">
                <div class="price">{{ price }}</div>
              </div>
            </div>
            <div v-if="!isLoading" class="actions">
              <Button v-if="activity.detailsUrl" class="book" btnType="grayed" @click="$emit('onDetailsClick')">
                <span class="btn-text">Details</span>
                <ExtLinkIcon class="icon-external-link"></ExtLinkIcon>
              </Button>
              <Button v-if="activity.bookingUrl" class="book" btnType="primary" @click="$emit('onBookClick')">
                <span class="btn-text">Book</span>
                <ExtLinkIcon class="icon-external-link icon-external-link--white"></ExtLinkIcon>
              </Button>
            </div>
          </div>
        </transition>
      </div>

      <slot name="actions"> </slot>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import VueCompositionAPI from '@vue/composition-api';
import Button from '@common/button/button.vue';
import ExtLinkIcon from '@components/svg/external-link.vue';
import ExpItem from '@components/explore-page/exp-list/exp-item.vue';

Vue.use(VueCompositionAPI);

import StarRating from 'vue-star-rating';
import {usePlaceCardLogic} from '@common/place-card-logic/place-card-logic';
// import { usePlaceCardLogic } from '@mobi-components/amex-poi-details-view';

export default {
  components: {
    ExpItem,
    StarRating,
    Button,
    ExtLinkIcon,
  },
  props: {
    activity: Object,
    isLoading: {
      type: Boolean,
      default: false,
    },
    isRatingShown: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    onSelectItem() {
      this.$emit('item-selected');
    },
    onShowDetails() {
      this.$emit('show-details');
    },
  },
  setup(props) {
    return usePlaceCardLogic(props);
  },
};
</script>

<style lang="scss" scoped>
.place-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  display: block;
}

.childActivity.place-card .image-placeholder {
  margin-left: 23px;
}

.childActivity.place-card:after,
.childActivity.place-card:before {
  content: '';
  position: absolute;
  top: 50%;
  left: 10px;
  height: 1px;
  width: 20px;
  background-color: var(--toneBw-5);
}
.childActivity + .childActivity.place-card:after {
  top: -100px;
  left: 10px;
  height: 175px;
  width: 1px;
}
.childActivity.place-card:after {
  top: -20px;
  left: 10px;
  height: 112px;
  width: 1px;
}
.parentActivity.place-card {
  position: relative;
  z-index: 1;
}

.place-card + .place-card {
  // border-top: 1px solid #e7e7e7;
  margin-top: 10px;
}

.place-card.loading {
  margin: 10px 0px;
}

.place-card:hover {
  & .amex-primary-btn {
    opacity: 1;
    z-index: 1;
    pointer-events: auto;
  }
  & .star-rating {
    /* opacity: 0; */
    pointer-events: none;
  }
}

.star-rating {
  align-items: start;
}

.placeImage:hover:after {
  content: 'View Details';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(7, 29, 59, 0.6);
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 118.14%;
  /* or 21px */

  text-align: center;
  letter-spacing: -0.005em;

  color: #ffffff;
}

.content {
  flex: 1;
  display: flex;
}

.image-placeholder {
  width: 150px;
  height: 150px;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  background-color: #f1f1f1;
  flex-shrink: 0;
  cursor: pointer;
}
.placeImage {
  width: 100%;
  height: 100%;

  & > img {
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
  }
}

.itinerary-card-tags {
  margin: 0 -5px;
}

.tag {
  position: relative;
  border-radius: 4px;
  font-size: 13px;
  padding: 8px 10px 6px;
  display: inline-block;
  margin: 4px;
  text-transform: capitalize;
  color: var(--recommendations-primary, #e9a43d);
  border: 1px solid #e9a43d;
}

.tag.selected {
  border: 1px solid var(--tags-selected-color);
  color: var(--tags-selected-color);
  padding: 8px 10px 6px 20px;
}

.tag.selected::before {
  content: '';
  position: absolute;
  top: 18px;
  left: 5px;
  margin: -8px -8px 0 0;
  opacity: 1;
  transform: none;
  width: 12px;
  height: 12px;
  -webkit-mask: url('./check.svg') no-repeat 50% 50%;
  // background-image: url('./check.svg');
  background-color: var(--tags-selected-color);
  background-size: 7px;
  background-position: 1px 1px;
  background-repeat: no-repeat;
}

.cost {
  font-size: 18px;
  line-height: 0.9em;
  display: flex;
  justify-content: space-between;
  flex: 1;
}

.selected-text {
  color: var(--btn-primary-color);
  margin-bottom: 5px;
}

.rating-wrapper {
  height: 30px;
}

.price-wrapper {
  margin-left: auto;
  font-style: normal;
  font-weight: 400;
  font-size: 24px;
  line-height: 118.14%;
  margin-top: 10px;
  /* or 28px */
  margin-top: 0;
  text-align: right;
  letter-spacing: -0.005em;

  color: #071d3b;

  display: flex;
  align-items: center;
}

.price {
  padding-right: 10px;
  padding-top: 2px;
  margin-right: 10px;
}

.details {
  margin-left: 30px;
}

.name {
  font-style: normal;
  font-size: 18px;
  line-height: 118.14%;
  letter-spacing: -0.005em;
  color: #071d3b;
  margin-bottom: 5px;
  font-weight: bold;
}
</style>

<style>
.vue-star-rating-rating-text {
  font-size: 12px;
  color: #a3abb6;
  position: relative;
  top: -3px;
}
</style>

<style lang="scss" scoped>
.place-card.loading {
  pointer-events: none;
}

.loading .image-placeholder {
  position: relative;
  background: #f1f1f1;
}

.loading .details {
  flex: 1;
}

.loading .name {
  background: #f1f1f1;
  width: 100%;
  height: 23px;
  border-radius: 10px;
  position: relative;
}

.loading .activity-description {
  background: #f1f1f1;
  width: 80%;
  height: 16px;
  border-radius: 10px;
  position: relative;
}

.activity-description {
  span {
    font-size: 14px;
    color: var(--font-color-light);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: 10px;
  }
}

.loading .price-wrapper:before {
  content: '';
  position: relative;
  top: 50%;
  width: 116px;
  height: 39px;
  background: #f1f1f1;
  border-radius: 10px;
  transform: translateY(-50%);
}

.loading .price-wrapper:after {
  content: '';
  position: relative;
  top: 50%;
  width: 71px;
  height: 39px;
  background: #f1f1f1;
  border-radius: 10px;
  transform: translateY(-50%);
}

.actions {
  display: flex;
  margin: 0 0 0 20px;
}

.book {
  min-width: 97px;
  height: 40px;
  padding: 0;
}

.book:nth-of-type(1) {
  margin-right: 10px;
  padding: 16px;
}

.icon-external-link {
  color: var(--btn-grayed-f-color);
  height: 14px;
  width: 10px;
  margin-left: 10px;
  margin-bottom: -2px;
  &--white {
    color: white;
  }
}
</style>
